import { ErrorLogComponent } from '@cian/error-log-component';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { CommercialPublishersBadges } from 'shared/components/CommercialPublishersBadges';
import { PriceAuctionContainer } from 'shared/components/PriceAuction';
import { NewbuildingPremiumBuilderInfo } from 'shared/containers/AuthorAsideContainerNew/parts';
import { BookingCalendarInputContainer } from 'shared/containers/BookingCalendarInputContainer';
import { BookingSummaryContainer } from 'shared/containers/BookingSummaryContainer';
import { MainPromo } from 'shared/containers/MainPromo';
import { AsideTrapContainerLoader } from 'shared/containers/valuationTraps/CPDTrapContainer';
import { useShowDailyrentBookingCalendar } from 'shared/hooks/useShowDailyrentBookingCalendar';
import { selectBookedFromDeveloperEnabled } from 'shared/selectors';
import { selectDailyrentActionType } from 'shared/selectors/dailyrentActionType';
import { selectIsMortgageBrokerBannerAvailable } from 'shared/selectors/mortgageBroker';
import { selectIsBookedFromDeveloper } from 'shared/selectors/newbuildingBookedFromDeveloper';
import { isCommercialSelector } from 'shared/selectors/offerData/offer/isCommercialSelector';
import { isPriceElectronicTradingSelector } from 'shared/selectors/price';
import { selectExtraPrices } from 'shared/selectors/purchaseTerms/purchaseTermsV2';
import { EActionType } from 'shared/types/dailyrent';

import { PriceContainer } from './Price';
import { PriceChangesContainer } from './PriceChanges';
import { AsideGroup } from '../../components/AsideSection/AsideGroup';
import { AsideItem } from '../../components/AsideSection/AsideItem';
import { AsideSection } from '../../components/AsideSection/AsideSection';
import { DealRent } from '../../components/DealRent';
import { OfferCardAsideContainer } from '../../components/aside/container';
import { AsideBanners } from '../../components/aside_banners/AsideBanners';
import { isDealRent as isDealRentSelector } from '../../selectors/isDealRent';
import { selectIsMortgageBannerEnabledIgnoreAgent } from '../../selectors/mortgage/selectIsMortgageBannerEnabled';
import { selectIsNewbuildingPremiumRate } from '../../selectors/newbuildingPremiumRate';
import { useApplicationContext } from '../ApplicationContext';
import { AsideContactsContainer } from '../AsideContacts/AsideContacts';
import { PhonesOpenedProvider } from '../AsideContacts/providers/PhonesOpened';
import { AuthorAsideContainer as AuthorAsideContainerNew } from '../AuthorAsideContainerNew';
import { FavoriteButtonContainer } from '../FavoriteButtonContainer';
import { GuestsControlContainer } from '../GuestsControlContainer';
import { LegalInfoContainer } from '../LegalInfoContainer';
import { MortgageSidebarBannerContainer } from '../MortgageSidebarBannerContainer';
import { MortgageWidgetContainer } from '../MortgageWidgetRedesignContainer';
import { NewbuildingBrokerSidebarBannerContainer } from '../NewbuildingBrokerSidebarBannerContainer';
import { NewbuildingBuildersFactsContainer } from '../NewbuildingBuildersFactsContainer';
import { NewbuildingRatingContainer } from '../NewbuildingRatingContainer';
import { OfferFactsInSidebarContainer } from '../OfferFactsInSidebarContainer';
import { OfferTitleSidebarContainer } from '../OfferTitleSidebarContainer';
import { PriceRequestContainer } from '../PriceRequestContainer';
import { PurchaseTermsSectionContainer } from '../PurchaseTermsV2Container/PurchaseTermsSectionContainer';
import { RentBlockContainer } from '../RentBlockContainer';
import { AgencyBrandingAsideCardContainer } from '../agencyBranding/AgencyBrandingAsideCard';

export const AsideRedesignedContainer = () => {
  const { logger } = useApplicationContext();
  const isDealRent = useSelector(isDealRentSelector);
  const isCommercial = useSelector(isCommercialSelector);
  const isPremiumNewbuilding = useSelector(selectIsNewbuildingPremiumRate);
  const isMortgageEnabled = useSelector(selectIsMortgageBannerEnabledIgnoreAgent);
  const isPriceElectronicTrading = useSelector(isPriceElectronicTradingSelector);
  const isMortgageBrokerBannerAvailable = useSelector(selectIsMortgageBrokerBannerAvailable);
  const actionType = useSelector(selectDailyrentActionType);
  const extraPrices = useSelector(selectExtraPrices);
  const isBookedFromDeveloper = useSelector(selectIsBookedFromDeveloper);
  const bookedFromDeveloperEnabled = useSelector(selectBookedFromDeveloperEnabled);

  const bookedFromDeveloperAvaliable = bookedFromDeveloperEnabled && isBookedFromDeveloper;

  const extraPricesShown = extraPrices && extraPrices.extraPrices.length > 0;
  const asideAdShown = !isPremiumNewbuilding && !bookedFromDeveloperAvaliable;
  const asideMortgageBannerShown =
    !isMortgageBrokerBannerAvailable && !isPremiumNewbuilding && !isCommercial && !bookedFromDeveloperAvaliable;

  const renderBookingCalendarAndGuestsInput = useShowDailyrentBookingCalendar();
  const showBookingSummary =
    actionType &&
    [EActionType.OtaBooking, EActionType.OtaCheckDates, EActionType.BookingRequestView].includes(actionType);

  return (
    <OfferCardAsideContainer>
      <AsideSection dataName="AsideMainInfo" withBorders={false}>
        <PhonesOpenedProvider>
          <AsideGroup>
            {isPremiumNewbuilding && (
              <AsideItem>
                <NewbuildingRatingContainer />
                <OfferTitleSidebarContainer />
              </AsideItem>
            )}
            <AsideItem>
              <PriceAuctionContainer />
            </AsideItem>
            <AsideItem isRedesign={true}>
              <PriceContainer />
              <FavoriteButtonContainer newButton={true} />
            </AsideItem>
            {extraPricesShown && (
              <AsideItem>
                <PurchaseTermsSectionContainer />
              </AsideItem>
            )}
            {isCommercial && (
              <AsideItem>
                <CommercialPublishersBadges />
              </AsideItem>
            )}
            {!isPriceElectronicTrading && !isPremiumNewbuilding && (
              <AsideItem>
                <PriceChangesContainer />
              </AsideItem>
            )}
            {renderBookingCalendarAndGuestsInput && (
              <AsideItem>
                <BookingCalendarInputContainer />
              </AsideItem>
            )}
            {renderBookingCalendarAndGuestsInput && (
              <AsideItem>
                <GuestsControlContainer />
              </AsideItem>
            )}
            {showBookingSummary && (
              <AsideItem>
                <BookingSummaryContainer />
              </AsideItem>
            )}
            <AsideItem>{<PriceRequestContainer />}</AsideItem>
          </AsideGroup>
          {!extraPricesShown && (
            <AsideGroup>
              <AsideItem>
                <MainPromo />
              </AsideItem>
              <AsideItem>{isMortgageEnabled && !bookedFromDeveloperAvaliable && <MortgageWidgetContainer />}</AsideItem>
              <AsideItem>
                <AsideTrapContainerLoader />
              </AsideItem>
            </AsideGroup>
          )}

          <AsideGroup>
            <AsideItem>
              <OfferFactsInSidebarContainer />
            </AsideItem>
          </AsideGroup>
          <AsideContactsContainer />
        </PhonesOpenedProvider>
      </AsideSection>
      <AsideSection withBorders={false} withSpaces={false} overflowed hideForPrint>
        <NewbuildingBrokerSidebarBannerContainer />
      </AsideSection>
      <AsideSection dataName="AuthorAside" withBorders={false} overflowed>
        <AuthorAsideContainerNew />
      </AsideSection>
      <AsideSection dataName="AuthorBrandingAside" withSpaces={false} withBorders={false} overflowed>
        <NewbuildingPremiumBuilderInfo />
        <AgencyBrandingAsideCardContainer />
        <NewbuildingBuildersFactsContainer />
      </AsideSection>
      {asideAdShown && (
        <AsideSection dataName="AsideAd" hideForPrint={true} withShadow={false} withBorders={false} withSpaces={false}>
          <ErrorLogComponent logger={logger}>
            <AsideBanners />
          </ErrorLogComponent>
        </AsideSection>
      )}

      <DealRent />
      {isDealRent && <RentBlockContainer />}

      <AsideSection dataName="AsideLegalInfo" withBorders={false} withShadow={false} withSpaces={false}>
        <LegalInfoContainer />
      </AsideSection>

      {asideMortgageBannerShown && (
        <AsideSection dataName="AsideMortgageBanner" hideForPrint={true} withShadow={false}>
          <MortgageSidebarBannerContainer />
        </AsideSection>
      )}
    </OfferCardAsideContainer>
  );
};
